import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/ui/password-input";
import {
  PasswordStrength,
  isPasswordValid,
} from "../components/auth/PasswordStrength";
import { SerpixWordmark } from "../components/brand/SerpixWordmark";
import { CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const pwValid = isPasswordValid(password);
  const pwMatch = confirm.length > 0 && password === confirm;
  const showMismatch = confirm.length > 0 && !pwMatch;
  const canSubmit = pwValid && pwMatch && token.trim().length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwValid) {
      setError(t("auth.pw_invalid_hint"));
      return;
    }
    if (!pwMatch) {
      setError(t("auth.pw_mismatch"));
      return;
    }
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
      <div className="w-full max-w-sm rounded-lg border border-zinc-200 bg-white p-8">
        <Link to="/" className="mb-6 inline-flex items-center gap-2">
          <SerpixWordmark size="sm" />
        </Link>
        <h1 className="mb-6 font-display text-2xl font-semibold">
          {t("auth.reset_password")}
        </h1>

        {done ? (
          <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-3 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4" />
            {t("auth.reset_success")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.reset_token")}
              </Label>
              <Input
                required
                value={token}
                onChange={(e) => setToken(e.target.value)}
                data-testid={TEST_IDS.resetTokenInput}
                className="mt-1 font-mono text-xs"
              />
              <p className="mt-1 text-xs text-zinc-500">
                {t("auth.reset_token_hint")}
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.new_password")}
              </Label>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={TEST_IDS.resetPasswordInput}
                testIdToggle="reset-password-toggle"
                className="mt-1"
              />
              <PasswordStrength password={password} hideWhenEmpty />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                {t("auth.confirm_password")}
              </Label>
              <PasswordInput
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid={TEST_IDS.resetConfirmInput}
                testIdToggle="reset-confirm-toggle"
                className="mt-1"
                aria-invalid={showMismatch ? "true" : "false"}
              />
              {showMismatch && (
                <p
                  className="mt-1 text-xs font-medium text-red-600"
                  data-testid="reset-password-mismatch"
                >
                  {t("auth.pw_mismatch")}
                </p>
              )}
            </div>
            {error && <div className="text-xs text-red-600">{error}</div>}
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid={TEST_IDS.resetSubmitBtn}
              className="w-full bg-gradient-to-r from-[#5B4CFB] to-[#7c5cf6] text-white shadow-lg shadow-violet-500/25 hover:from-[#4f40f0] hover:to-[#6f4df0]"
            >
              {t("auth.reset_submit")}
            </Button>
          </form>
        )}

        <Link
          to="/login"
          className="mt-6 block text-xs text-zinc-500 hover:text-zinc-900"
        >
          ← {t("auth.login")}
        </Link>
      </div>
    </div>
  );
}
